import * as React from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import { SplitPanes, Pane } from '../../../core/components/SplitPanes.tsx';
import {
    OutputMapperEditorProvider,
    useOutputMapperEditor,
    TargetFieldsPanel,
    ChainBuilderPanel,
} from '../OutputMapperEditor/index.ts';
import type { OutputMapperConfig } from '../OutputMapperEditor/types.ts';

interface InlineVisualEditorProps {
    config: OutputMapperConfig;
    onChange: (config: OutputMapperConfig) => void;
    initialTarget?: string | null;
}

const EditorContent = ({ onChange }: { onChange: (config: OutputMapperConfig) => void }) => {
    const { config, isDirty } = useOutputMapperEditor();
    const lastSyncedConfigRef = React.useRef<OutputMapperConfig | null>(null);

    // Sync changes back to parent when config changes
    // Use a ref to prevent re-syncing the same config when onChange reference changes
    React.useEffect(() => {
        if (isDirty && config !== lastSyncedConfigRef.current) {
            lastSyncedConfigRef.current = config;
            onChange(config);
        }
    }, [config, isDirty, onChange]);

    return (
        <DndProvider backend={HTML5Backend}>
            <div className="flex flex-col h-full">
                <div className="flex-1 min-h-0">
                    <SplitPanes id="inline-output-mapper" initialWidths={[35, 65]}>
                        <Pane className="bg-surface-weak border-r border-line-weak overflow-hidden">
                            <TargetFieldsPanel />
                        </Pane>
                        <Pane className="bg-surface-weak overflow-hidden">
                            <ChainBuilderPanel />
                        </Pane>
                    </SplitPanes>
                </div>
            </div>
        </DndProvider>
    );
};

const InlineVisualEditor = ({ config, onChange, initialTarget }: InlineVisualEditorProps) => {
    return (
        <OutputMapperEditorProvider initialConfig={config} initialTarget={initialTarget}>
            <EditorContent onChange={onChange} />
        </OutputMapperEditorProvider>
    );
};

export default InlineVisualEditor;
